import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../Header';

/**
 * VISUAL BRANDING & CONTENT PAGE
 * Theme: 60% Aqua Haze (#F0F4F8) | 30% River Bed (#4A5568) | 10% Accent (#2B6CB0)
 * Headline Authority: #020617
 */
const VisualBranding = () => {
  return (
    <div className="min-h-screen bg-[#F0F4F8] text-[#4A5568] font-sans">
      <Header />
      
      {/* 1. HERO SECTION - High Authority */}
      <section className="relative pt-32 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#2B6CB0] font-black text-[10px] uppercase tracking-[0.4em] mb-6">Identity & Content Engine</p>
            <h1 className="text-5xl md:text-8xl font-black mb-8 tracking-tighter text-[#020617] uppercase leading-[0.9]">
              Visual Branding <br className="hidden md:block" /> 
              <span className="text-[#2B6CB0]">&</span> Social Systems
            </h1>
            <p className="text-xl text-[#4A5568] max-w-3xl mx-auto mb-12 leading-relaxed font-medium">
              We capture the shots, edit the reels, and build the identity systems that define market leaders. 
              From high-end social management to complete brand guidelines.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/contact" className="bg-[#2B6CB0] text-white px-10 py-5 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-[#020617] transition-all shadow-xl shadow-[#2B6CB0]/20">
                Inquire About Custom Packages
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-8 h-[1px] bg-[#2B6CB0]"></div>
                <span className="text-[#4A5568] text-[10px] font-black uppercase tracking-widest">
                  Weekly Posting • Photography • Video
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. FEATURED: SOCIAL CONTENT ENGINE */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="bg-[#F0F4F8] rounded-[3rem] p-8 md:p-20 border border-[#4A5568]/5 relative overflow-hidden group">
            <div className="grid lg:grid-cols-2 gap-16 items-center relative z-10">
              <div className="order-2 lg:order-1 grid grid-cols-2 gap-6">
                <div className="space-y-6">
                  <img src="https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?q=80&w=600" alt="Professional Content" className="rounded-3xl h-64 w-full object-cover shadow-lg grayscale hover:grayscale-0 transition-all duration-500" />
                  <div className="bg-[#2B6CB0] p-8 rounded-3xl text-white">
                    <p className="font-black text-2xl uppercase tracking-tighter italic">REELS</p>
                    <p className="text-xs font-bold uppercase tracking-widest opacity-80">Short-form mastery</p>
                  </div>
                </div>
                <img src="https://images.unsplash.com/photo-1493612276216-ee3925520721?q=80&w=600" alt="Instagram Management" className="rounded-3xl h-full w-full object-cover shadow-lg mt-12 grayscale hover:grayscale-0 transition-all duration-500" />
              </div>
              
              <div className="order-1 lg:order-2">
                <h2 className="text-4xl md:text-5xl font-black mb-8 tracking-tighter text-[#020617] uppercase leading-none">
                  Social Media <br/> Management
                </h2>
                <p className="text-lg text-[#4A5568] mb-10 font-medium leading-relaxed">
                  Available in <span className="text-[#2B6CB0] font-bold">Custom Packages</span>, we provide a full-service content engine. 
                  This includes professional production, short-form video (Reels), 
                  and full account management with consistent weekly posting.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-8 gap-x-8 mb-10">
                  {[
                    { title: 'Professional Photos', desc: 'On-site brand shoots.' },
                    { title: 'Weekly Posting', desc: 'Consistent grid presence.' },
                    { title: 'Custom Video', desc: 'High-engagement Reels.' },
                    { title: 'Full Management', desc: 'Captions & scheduling.' }
                  ].map((item, i) => (
                    <div key={i} className="border-l-2 border-[#2B6CB0] pl-4">
                      <h4 className="text-[#020617] font-black text-[11px] uppercase tracking-widest mb-1">{item.title}</h4>
                      <p className="text-[#4A5568] text-xs font-medium">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. CORE IDENTITY */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center mb-32">
            <div>
              <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tighter text-[#020617] uppercase leading-[0.85]">
                Complete Visual <br/> <span className="text-[#2B6CB0]">Identity Systems</span>
              </h2>
              <p className="text-lg text-[#4A5568] mb-8 leading-relaxed font-medium">
                Logo suites, typography, and color theory are just the start. We build the layout rules and supporting graphics that ensure consistency across websites, product UI, and campaigns.
              </p>
              <div className="space-y-4">
                 <p className="flex items-center gap-4 text-xs font-black uppercase tracking-widest text-[#020617]">
                    <span className="w-2 h-2 bg-[#2B6CB0] rounded-full"></span> Scalable Brand Guidelines
                 </p>
                 <p className="flex items-center gap-4 text-xs font-black uppercase tracking-widest text-[#020617]">
                    <span className="w-2 h-2 bg-[#2B6CB0] rounded-full"></span> Marketing Asset Kits
                 </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -bottom-6 -right-6 w-full h-full border-2 border-[#2B6CB0]/20 rounded-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1634942537034-2531766767d1?auto=format&fit=crop&q=80&w=800"
                alt="Brand Identity"
                className="relative w-full h-[450px] object-cover rounded-2xl shadow-2xl grayscale"
              />
            </div>
          </div>

{/* Social Media Services */}
<div className="mb-32">
  <h2 className="text-2xl font-black mb-12 tracking-widest text-[#020617] uppercase border-b-4 border-[#2B6CB0] inline-block pb-2">
    Social Media Services
  </h2>

  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
    {[
      {
        title: 'Content Design',
        desc: 'High-quality, on-brand visuals crafted for engagement and clarity across platforms.'
      },
      {
        title: 'Post Templates',
        desc: 'Reusable social templates for consistent branding and faster content creation.'
      },
      {
        title: 'Creative Direction',
        desc: 'Visual storytelling aligned with your brand voice and marketing goals.'
      },
      {
        title: 'Platform Optimization',
        desc: 'Designs tailored for Instagram, Facebook, LinkedIn, and TikTok formats.'
      },
      {
        title: 'Campaign Creatives',
        desc: 'Eye-catching visuals for launches, promotions, and paid campaigns.'
      },
      {
        title: 'Brand Consistency',
        desc: 'Every post aligned with your colors, typography, and overall brand identity.'
      }
    ].map((service) => (
      <div
        key={service.title}
        className="bg-white p-10 rounded-xl border border-[#F0F4F8] shadow-sm hover:translate-y-[-5px] transition-all duration-300 group"
      >
        <h3 className="text-xs font-black mb-4 text-[#2B6CB0] uppercase tracking-[0.2em] group-hover:text-[#020617]">
          {service.title}
        </h3>
        <p className="text-[#4A5568] text-sm leading-relaxed font-medium">
          {service.desc}
        </p>
      </div>
    ))}
  </div>
</div>


{/* 4. PROCESS SECTION - Architectural Style */}
<section className="py-24 bg-white">
  <div className="max-w-7xl mx-auto px-6">
    <h2 className="text-5xl md:text-6xl font-black mb-20 tracking-tighter text-[#020617] uppercase">
      The <span className="text-[#2B6CB0]">Workflow</span>
    </h2>

    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#F0F4F8]">
      {[
        { step: '01', title: 'Discovery', desc: 'Understanding your brand story and social goals.' },
        { step: '02', title: 'Strategy', desc: 'Analyzing the market and planning content pillars.' },
        { step: '03', title: 'Design', desc: 'Creating the logo, assets, and shooting content.' },
        { step: '04', title: 'Launch', desc: 'Implementing guidelines and starting weekly posts.' }
      ].map((item, index) => (
        <div 
          key={item.step} 
          className="group relative p-10 border-r border-b border-[#F0F4F8] last:border-r-0 hover:bg-[#F0F4F8]/50 transition-all duration-500"
        >
          {/* Step Number with Accent Color */}
          <div className="flex justify-between items-start mb-12">
            <span className="text-5xl font-black text-[#F0F4F8] group-hover:text-[#2B6CB0] transition-colors duration-500">
              {item.step}
            </span>
            <div className="w-8 h-8 rounded-full border border-[#F0F4F8] flex items-center justify-center group-hover:bg-[#2B6CB0] group-hover:border-[#2B6CB0] transition-all">
               <div className="w-1.5 h-1.5 bg-[#2B6CB0] group-hover:bg-white rounded-full" />
            </div>
          </div>

          <div className="relative z-10">
            <h3 className="text-xl font-black mb-4 text-[#020617] uppercase tracking-tight">
              {item.title}
            </h3>
            {/* Secondary Color Text with better line height */}
            <p className="text-[#4A5568] text-sm font-medium leading-relaxed">
              {item.desc}
            </p>
          </div>

          {/* Bottom Accent Bar - only visible on hover */}
          <div className="absolute bottom-0 left-0 w-0 h-1 bg-[#2B6CB0] group-hover:w-full transition-all duration-700" />
        </div>
      ))}
    </div>
  </div>
</section>

          {/* Final CTA Card */}
          <div className="text-center bg-[#020617] p-16 rounded-[3rem] shadow-2xl relative overflow-hidden">
             <div className="absolute top-0 right-0 w-96 h-96 bg-[#2B6CB0] opacity-5 rounded-full -mr-48 -mt-48"></div>
            <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tighter text-white uppercase leading-tight">
              Elevate Your <br className="md:hidden" /> Visuals
            </h2>
            <p className="text-xl text-white/60 mb-12 max-w-2xl mx-auto font-medium leading-relaxed">
              Ready for a custom package that handles your branding and social presence from A to Z?
            </p>
            <Link to="/contact" className="bg-[#2B6CB0] text-white px-12 py-5 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white hover:text-[#020617] transition-all inline-block shadow-xl">
              Start Your Custom Project
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisualBranding;